"use client";

import StudentTestimonialCard from "@/components/molecules/StudentTestimonialCard";
import EmblaCarousel from "@/components/organisms/carousels/Carousel2";
import Carousel3 from "@/components/organisms/carousels/Carousel3";
import {
  cardClick,
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  navigationClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useCallback, useMemo, useRef } from "react";

interface TestimonialItem {
  id: string;
  name: string;
  designation: string;
  description: string;
  image?: string;
  nameTextColor?: string;
  designationTextColor?: string;
  descriptionTextColor?: string;
  videoUrl?: string;
  video?: string;
  altImage?: string;
}

const StudentTestimonials1 = ({ data }: { data: any }) => {
  const isMobile = useIsMobile(800);
  const ref = useRef<HTMLElement>(null);

  // Extract data from API response - MUST be done before early returns
  const {
    title,
    title_text_color,
    background_color = "",
    card_color,
    metadata,
    left_navigation_icon,
    left_navigation_icon_alt,
    right_navigation_icon,
    right_navigation_icon_alt,
  } = data?.data || {};

  // Map and filter API metadata to testimonial items format - MUST be called before early returns
  const testimonials: TestimonialItem[] = useMemo(
    () =>
      metadata
        ?.filter((item: any) => item.meta_status) // Only include active items
        ?.sort((a: any, b: any) => a.meta_priority - b.meta_priority) // Sort by priority
        ?.map((item: any) => ({
          id: item.id,
          name: item.meta_name,
          designation: item.meta_designation,
          description: item.meta_description,
          image: item.meta_desktop_image || item.meta_mobile_image,
          nameTextColor: item.meta_name_text_color,
          designationTextColor: item.meta_designation_text_color,
          descriptionTextColor: item.meta_description_text_color,
          videoUrl: item.meta_video_url,
          video: item.meta_video,
          altImage: item.meta_alt_image,
        })) || [],
    [metadata],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // Track widget viewed - MUST be called before early returns
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "student_testimonials_section");
  //   }
  // }, []);

  // Handle navigation click with tracking
  const handleNavigationClick = useCallback(
    (direction: "prev" | "next", currentIndex: number) => {
      navigationClick({
        section: "student_testimonials_section",
        component: "carousel_navigation",
        label: direction === "prev" ? "previous" : "next",
        index: String(currentIndex),
      });
    },
    [],
  );

  // Handle card click with tracking
  const handleCardClick = useCallback(
    (testimonial: TestimonialItem, index: number) => {
      cardClick({
        section: "student_testimonials_section",
        component: "testimonial_card",
        label: testimonial.name || `Testimonial ${index + 1}`,
        parent: title,
        redirectLink: "",
        pageKey: "",
      });
    },
    [title],
  );

  // Early returns AFTER all hooks are called
  if (!data?.data || !data?.data?.status) {
    return null;
  }

  // If no data, don't render anything
  if (!title || !testimonials || testimonials.length === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn(
        "flex w-full flex-col items-center justify-center",
        "xs:py-8",
        "sm:gap-6 sm:py-10",
      )}
      style={
        {
          // @ts-ignore
          "--title-color": title_text_color || "#000000",
          ...(background_color ? { backgroundColor: background_color } : {}),
        } as React.CSSProperties
      }
    >
      <h2
        className={cn(
          "text-heading-6-semibold",
          "sm:text-heading-5-semibold sm:max-w-[440px]",
          "md:text-heading-4-semibold md:max-w-[510px]",
          "lg:text-new-heading-34-semibold lg:max-w-[740px]",
          "xl:text-new-heading-42-semibold xl:max-w-[740px]",
          "line-clamp-1",
          "type-h3",

          "xs:px-5 sm:px-0",
        )}
        style={{ color: "var(--title-color)" }}
      >
        {title}
      </h2>
      {isMobile ? (
        <section className="w-screen max-w-[100vw] overflow-hidden">
          <Carousel3
            left_navigation_icon={left_navigation_icon}
            left_navigation_icon_alt={left_navigation_icon_alt}
            right_navigation_icon={right_navigation_icon}
            right_navigation_icon_alt={right_navigation_icon_alt}
            onNavigationClick={handleNavigationClick}
          >
            {testimonials.map((item, index) => (
              <StudentTestimonialCard
                altImage={item.altImage}
                videoUrl={item.videoUrl}
                video={item.video}
                key={item.id}
                data={item}
                cardColor={card_color}
                onClick={() => handleCardClick(item, index)}
              />
            ))}
          </Carousel3>
        </section>
      ) : (
        <div
          className={cn(
            "flex items-center justify-center",
            "sm:w-[1008px]",
            "md:w-[1158px]",
            "lg:w-[1358px]",
            "xl:w-[1538px]",
          )}
        >
          <EmblaCarousel
            options={{ align: "center", loop: true }}
            left_navigation_icon={left_navigation_icon}
            left_navigation_icon_alt={left_navigation_icon_alt}
            right_navigation_icon={right_navigation_icon}
            right_navigation_icon_alt={right_navigation_icon_alt}
            onNavigationClick={handleNavigationClick}
          >
            {testimonials.map((item, index) => (
              <StudentTestimonialCard
                altImage={item.altImage}
                videoUrl={item.videoUrl}
                video={item.video}
                key={item.id}
                data={item}
                cardColor={card_color}
                onClick={() => handleCardClick(item, index)}
              />
            ))}
          </EmblaCarousel>
        </div>
      )}
    </section>
  );
};

export default StudentTestimonials1;
